import {
  ORDER_TYPE,
  PACKAGE_TYPES,
  PASS_DISPLAY_TYPE,
  SUBSCRIPTION_PERIODS,
  URL_QUERIES,
} from '@/const';
import {parseStringBySeparatorToHtml} from '@/utils/helpers';
import Vue from 'vue';
import CheckIcon from '@/assets/img/CheckIcon.vue';
import XMarkIcon from '@/assets/img/XMarkIcon.vue';
import {BUY, LOGIN, SELECT_PACKAGE, UPCOMING} from '@/pages';
const MONTHS_IN_YEAR = 12;
const HOURS_IN_DAY = 24;

/**
 * Converts the VOD access time to days.
 * @param {number} time - The VOD access time in hours.
 * @return {string} - The VOD access time in days and hours.
 */
export function convertVodAccessTimeToDays({vod_access_time: time} = {}) {
  const day = Math.abs(time / HOURS_IN_DAY);
  const dayText = `${day} ${day === 1 ? 'day' : 'days'}`;
  const hrs = time % HOURS_IN_DAY;
  const hrsText = hrs ? ` ${hrs} hrs` : '';

  return `${dayText}${hrsText}`;
}

/**
 * Returns the link for the pass.
 * @param {Object} item - The pass item.
 * @param {Object} route - The route object.
 * @return {Object} - The link object for the pass.
 */
export function passLink(item, route) {
  if (item.passType === ORDER_TYPE.package && !item.base_price) {
    return {
      name: LOGIN.name,
    };
  }
  if (item.type === PACKAGE_TYPES.SINGLE) {
    if (route.name === SELECT_PACKAGE.name) {
      return {
        name: BUY.name,
        query: {
          [URL_QUERIES.SINGLE_PACKAGE_ID]: item.id,
          [URL_QUERIES.EVENT_ID]: route?.params?.id,
        },
      };
    } else {
      return {
        name: UPCOMING.name,
        query: {
          [URL_QUERIES.PACKAGE_ID]: item.id,
        },
      };
    }
  }
  return {
    name: BUY.name,
    query: {
      [`${item.passType}_id`]: item.id,
    },
  };
}

/**
 * Returns the buttons for the pass.
 * @param {Object} params - The parameters object.
 * @param {Object} params.item - The pass item.
 * @param {boolean} params.isLogged - Whether the user is logged in.
 * @param {string} params.currency - The currency string.
 * @param {Object} params.route - The route object.
 * @return {Array} - The array of button objects.
 */
export function getPassButtons({
  item,
  isLogged,
  currency,
  route,
}) {
  return (item?.extraPasses || [item]).map((pass) => {
    return {
      link: passLink(pass, route),
      key: `${pass.id}${pass.passType}buttons`,
      disabled: pass.purchased,
      buttonText: getButtonTitle(pass, currency, isLogged),
    };
  });
}
/**
 * Returns the title for the price of a pass.
 * @param {Object} pass
 * @param {string} currency
 * @param {Boolean} isLogged
 * @return {{html: string, title: string}}
 */
export function getButtonTitle(pass, currency, isLogged) {
  if (pass.purchased || !pass.base_price) {
    const loggedText = isLogged ?
      Vue.prototype.$t('Watch now') :
      Vue.prototype.$t('Register to Watch');

    return {
      title: loggedText,
      html: `<span>${loggedText}</span>`,
    };
  }
  if (pass.passType === ORDER_TYPE.subscription) {
    let price = Number(pass.base_price).toFixed(2);
    let textPeriod = Vue.prototype.$t('Pass');
    const paidPeriod = Number(pass.period) === 1 ? `/${Vue.prototype.$t('month')}`:'';

    if (pass.offer === SUBSCRIPTION_PERIODS.ANNUAL) {
      textPeriod = Vue.prototype.$t('Annual');
      price = (pass.base_price / MONTHS_IN_YEAR).toFixed(2);
    } else if (Number(pass.period) === 1) {
      textPeriod = Vue.prototype.$t('Monthly');
    }

    return {
      // eslint-disable-next-line max-len
      title: `${Vue.prototype.$t('Buy')} ${textPeriod} $${price} ${currency}${paidPeriod}`,
      html: `<span>${Vue.prototype.$t('Buy')} ${textPeriod}</span>
        <span>$${price} ${currency}${paidPeriod}</span>`,
    };
  }
  if (pass.passType === ORDER_TYPE.package && pass.type === PACKAGE_TYPES.SINGLE) {
    return {
      title: `${Vue.prototype.$t('Buy Game')} $${pass.base_price} ${currency}`,
      html: `<span>${Vue.prototype.$t('Buy Game')}</span>
        <span>$${pass.base_price} ${currency}</span>`,
    };
  }
  return {
    // eslint-disable-next-line max-len
    title: `${Vue.prototype.$t('Buy Event Pass')} $${pass.base_price} ${currency}`,
    html: `<span>${Vue.prototype.$t('Buy Event Pass')}</span>
      <span>$${pass.base_price} ${currency}</span>`,
  };
}

export const getDataArray = (passes, cb, {
  collapseWithPrevIfEqual,
  displayType = PASS_DISPLAY_TYPE.TABLE,
} = {}) => {
  const results = [];
  passes.forEach((pass) => {
    const prev = results[results.length - 1];
    const next = cb(pass, {displayType}) || '';
    if (
      prev && collapseWithPrevIfEqual &&
      JSON.stringify(prev.data) === JSON.stringify(next)
    ) {
      prev.colspan ? prev.colspan++ : (prev.colspan = 2);
      !prev.styles['background-color'] &&
        (prev.styles['background-color'] = pass['package-background']);
      !prev.styles['color'] && (prev.styles['color'] = pass['package-text-color']);
    } else {
      results.push({
        data: next,
        cellKey: `${pass.passType}_${pass.id}`,
        styles: {
          'background-color': pass['package-background'],
          'color': pass['package-text-color'],
        },
      });
    }
  });
  return results;
};
const passTag = ({tag} = {}) => ({text: tag});
const passName = (pass = {}) => {
  const langObj = Object.fromEntries(
      Object.entries(pass.name_lang || {})
          .map(([key, value]) => [key.toLowerCase(), value]),
  );
  const name = langObj?.[Vue.i18n.locale().toLowerCase()] || pass.name;
  return {
    html: parseStringBySeparatorToHtml(name, '\\r\\n', 'span'),
    className: 'flex-column-child',
  };
};
const liveOnly = () => {
  return {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
  };
};
const liveOnDemand = (pass) => {
  if (pass.passType === ORDER_TYPE.subscription) {
    return {
      component: CheckIcon,
      className: 'yes',
      displayForCard: true,
    };
  }
  return Boolean(pass['is_access_live'] && pass['is_access_vod'])? {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
  } : {
    component: XMarkIcon,
    className: 'no',
    displayForCard: false,
  };
};
const onDemand = (pass, {displayType}) => {
  const daysText = convertVodAccessTimeToDays(
    pass.vod_access_time? pass : {vod_access_time: 30 * HOURS_IN_DAY},
  );
  const textForCard = displayType === PASS_DISPLAY_TYPE.CARD &&
    dataTextTemplates.onDemand({daysText});
  if (PACKAGE_TYPES.SINGLE === pass.type) {
    return {
      text: displayType === PASS_DISPLAY_TYPE.TABLE? daysText: '',
      displayForCard: true,
      component: displayType === PASS_DISPLAY_TYPE.CARD? CheckIcon: undefined,
      textForCard,
    };
  }
  if (pass.passType === ORDER_TYPE.subscription) {
    return {
      component: CheckIcon,
      className: 'yes',
      displayForCard: true,
      textForCard,
    };
  }
  if (!pass['is_access_vod']) {
    return {
      component: XMarkIcon,
      className: 'no',
      displayForCard: false,
    };
  }
  return {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
    textForCard,
  };
};
const dvr = (pass) => {
  return pass['live_dvr_ability']? {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
  } : {
    component: XMarkIcon,
    className: 'no',
    displayForCard: false,
  };
};
const share = (pass) => {
  return pass['clip_ability']? {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
  } : {
    component: XMarkIcon,
    className: 'no',
    displayForCard: false,
  };
};
const download = (pass) => {
  return pass['download_ability']? {
    component: CheckIcon,
    className: 'yes',
    displayForCard: true,
  } : {
    component: XMarkIcon,
    className: 'no',
    displayForCard: false,
  };
};

const logins = (pass, {displayType}) => {
  return {
    text: pass['sessions_limit'] || 'Unlimited',
    displayForCard: true,
    component: displayType === PASS_DISPLAY_TYPE.CARD? CheckIcon: undefined,
  };
};

export const tableHeaders = [
  {
    name: '',
    key: 'tag',
    callback: (passes) => getDataArray(passes, passTag, {
      collapseWithPrevIfEqual: true,
    }),
    className: 'no-border',
  },
];

const rowsNames = {
  liveOnly: 'Live games',
  liveOnDemand: 'Live & On-Demand',
  OnDemand: 'Access to 30-day On-Demand',
  rewind: 'Live DVR (pause & rewind live games)',
  share: 'Clip & share highlights to social, text & email',
  download: 'Download games',
  logins: 'Number of logins<sup>*</sup>',
};

export const additionalInfo = {
  logins: {
    text: '<sup>*</sup>Number of simultaneous logins',
    className: 'no-border',
  },
};

const dataTextTemplates = {
  onDemand: (data) => (`${data.daysText} Access to On-Demand`),
};

export const tableRows = [
  {
    name: '',
    key: 'name',
    needStyle: true,
    callback: (passes) => getDataArray(passes, passName),
  },
  {
    name: rowsNames.liveOnly,
    key: 'liveOnly',
    callback: (passes) => getDataArray(passes, liveOnly),
  },
  {
    name: rowsNames.liveOnDemand,
    key: 'liveOnDemand',
    callback: (passes) => getDataArray(passes, liveOnDemand),
  },
  {
    name: rowsNames.OnDemand,
    key: 'OnDemand',
    callback: (passes, options) => getDataArray(passes, onDemand, options),
  },
  {
    name: rowsNames.rewind,
    key: 'rewind',
    callback: (passes) => getDataArray(passes, dvr ),
  },
  {
    name: rowsNames.share,
    key: 'share',
    callback: (passes) => getDataArray(passes, share),
  },
  {
    name: rowsNames.download,
    key: 'download',
    callback: (passes) => getDataArray(passes, download),
  },
  {
    name: rowsNames.logins,
    key: 'logins',
    callback: (passes, options) => getDataArray(passes, logins, options),
  },
];

/**
 * Groups passes by a specified field name.
 * @param {Array} passes - The array of passes to group.
 * @param {string} fieldName - The field name to group by.
 * @return {Array} - The grouped passes.
 */
export function groupByField(passes, fieldName) {
  const groups = {};
  const DEFAULTS = [];

  passes.forEach((pass) => {
    const value = pass[fieldName];
    if (!value) {
      DEFAULTS.push(pass);
      return;
    }
    if (!groups[value]) {
      groups[value] = [];
    }
    groups[value].push(pass);
  });

  return [...Object.values(groups), DEFAULTS].flat(Infinity);
}
